/**
 * Who the company is, and which statutory duties it carries.
 *
 * The second half is what the compliance calendar is built from. Every duty is
 * off until somebody switches it on, deliberately: raising deadlines nobody
 * owes is how a calendar teaches people to ignore it, and the deadlines they
 * then miss are the real ones.
 *
 * A duty is only half of what schedules anything. The flag says the business is
 * registered; the GSTINs on the locations say how many returns that actually
 * means. A flag with no fact behind it schedules nothing, which is why this
 * screen says so next to the switch rather than leaving somebody to wonder why
 * their calendar is empty.
 *
 * Edits are held on the page until Save; the bar that offers it appears only
 * when something has changed, so an unsaved switch is never mistaken for one
 * that took effect.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Stat } from '../components/ui'

interface Profile {
  id: string
  name: string
  legal_name: string
  pan: string | null
  cin: string | null
  fy_start_month: number
  base_currency: string
  city: string | null
  state_code: string | null
  constitution: string
  pf_applicable: boolean
  esi_applicable: boolean
  pt_state: string | null
  ptrc_periodicity: string | null
  ptec_applicable: boolean
  tds_deductor: boolean
  gst_registered: boolean
  advance_tax_applicable: boolean
  msme_form1_applicable: boolean
  has_payroll: boolean
}

interface Location {
  id: string
  name: string
  gstin: string | null
  is_active: boolean
}

const CONSTITUTIONS = [
  'company',
  'llp',
  'partnership',
  'proprietorship',
  'trust',
  'huf',
  'individual',
  'other',
]

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

interface Duty {
  key: keyof Profile
  label: string
  note: string
}

const DUTIES: Duty[] = [
  {
    key: 'tds_deductor',
    label: 'Deducts tax at source',
    note: 'Monthly deposits and quarterly returns. March deposits get until 30 April.',
  },
  {
    key: 'gst_registered',
    label: 'Registered for GST',
    note: 'GSTR-1 and 3B per registration, per month. Needs a GSTIN on a location.',
  },
  {
    key: 'advance_tax_applicable',
    label: 'Pays advance tax',
    note: 'Four instalments on the 15th of June, September, December and March.',
  },
  {
    key: 'msme_form1_applicable',
    label: 'Files MSME Form-1',
    note: 'Half-yearly to the MCA. A Companies Act duty, so it needs the constitution to be a company.',
  },
  {
    key: 'pf_applicable',
    label: 'Owes provident fund',
    note: 'Monthly contributions and the electronic return.',
  },
  {
    key: 'esi_applicable',
    label: 'Owes ESI',
    note: 'Monthly contributions on wages up to the ceiling.',
  },
  {
    key: 'ptec_applicable',
    label: 'Holds a PTEC',
    note: "The proprietor's own profession tax, annually.",
  },
  {
    key: 'has_payroll',
    label: 'Runs a payroll',
    note: 'Adds a separate salary-TDS deadline. This system has no payroll of its own.',
  },
]

//: Only the fields the form edits, in the order the API takes them, so
//: "changed" means a difference somebody could save.
const EDITED: (keyof Profile)[] = [
  'name',
  'legal_name',
  'pan',
  'cin',
  'city',
  'state_code',
  'constitution',
  'pf_applicable',
  'esi_applicable',
  'pt_state',
  'ptrc_periodicity',
  'ptec_applicable',
  'tds_deductor',
  'gst_registered',
  'advance_tax_applicable',
  'msme_form1_applicable',
  'has_payroll',
]

export default function Company() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Profile | null>(null)

  const profile = useQuery({
    queryKey: ['org-profile'],
    queryFn: () => api.get<Profile | null>('/org/profile'),
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
  })

  const save = useMutation({
    mutationFn: (body: unknown) => api.put<Profile>('/org/profile', body),
    onSuccess: (saved) => {
      setDraft(saved)
      void queryClient.invalidateQueries({ queryKey: ['org-profile'] })
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['duties'] })
    },
  })

  useEffect(() => {
    if (profile.data && draft === null) setDraft(profile.data)
  }, [profile.data, draft])

  if (profile.isLoading) return <Loading what="Loading the company" />

  const blank: Profile = {
    id: '',
    name: '',
    legal_name: '',
    pan: null,
    cin: null,
    fy_start_month: 4,
    base_currency: 'INR',
    city: null,
    state_code: null,
    constitution: 'company',
    pf_applicable: false,
    esi_applicable: false,
    pt_state: null,
    ptrc_periodicity: null,
    ptec_applicable: false,
    tds_deductor: false,
    gst_registered: false,
    advance_tax_applicable: false,
    msme_form1_applicable: false,
    has_payroll: false,
  }
  const saved = profile.data ?? blank
  const value = draft ?? saved
  const editable = can('org.profile.configure')
  const set = <K extends keyof Profile>(key: K, v: Profile[K]) => setDraft({ ...value, [key]: v })
  const changed = EDITED.filter((k) => (value[k] ?? null) !== (saved[k] ?? null))

  const gstins = [
    ...new Set(
      (locations.data ?? [])
        .filter((l) => l.is_active && l.gstin)
        .map((l) => (l.gstin ?? '').toUpperCase()),
    ),
  ]
  const dutiesOn = DUTIES.filter((d) => Boolean(saved[d.key]))

  //: Why a switched-on duty schedules nothing, when it does not.
  const blocked = (duty: Duty): string | null => {
    if (!value[duty.key]) return null
    if (duty.key === 'gst_registered' && gstins.length === 0)
      return 'No active location carries a GSTIN, so nothing is being scheduled.'
    if (duty.key === 'msme_form1_applicable' && value.constitution !== 'company')
      return `The constitution is ${value.constitution}, so this schedules nothing.`
    return null
  }

  return (
    <>
      <ErrorNote error={profile.error ?? save.error} />

      {profile.data === null && (
        <Note kind="warn">
          No company has been set up. Nothing about what the business owes can be worked out until
          it knows who it is, so the compliance calendar is empty until this is filled in.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Company"
          value={saved.name || '—'}
          note={saved.legal_name ? `${saved.legal_name} · ${saved.constitution}` : 'not set up'}
        />
        <Stat
          label="Statutory duties on"
          value={`${dutiesOn.length} of ${DUTIES.length}`}
          note="each one puts deadlines on the compliance calendar"
        />
        <Stat
          label="GST registrations"
          value={String(gstins.length)}
          note={gstins.length ? gstins.join(', ') : 'no active location carries a GSTIN'}
        />
        <Stat
          label="Financial year"
          value={`from ${MONTHS[(saved.fy_start_month || 4) - 1]}`}
          note={`books kept in ${saved.base_currency}`}
        />
      </div>

      <Card title="Who we are" hint="as it appears on statutory documents">
        <div className="row">
          <Field label="Name">
            <input
              value={value.name}
              disabled={!editable}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field label="Legal name" caption="as it appears on statutory documents">
            <input
              value={value.legal_name}
              disabled={!editable}
              onChange={(e) => set('legal_name', e.target.value)}
            />
          </Field>
          <Field label="Constitution" caption="decides which Companies Act duties apply">
            <select
              value={value.constitution}
              disabled={!editable}
              onChange={(e) => set('constitution', e.target.value)}
            >
              {CONSTITUTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="PAN">
            <input
              className="mono"
              value={value.pan ?? ''}
              disabled={!editable}
              onChange={(e) => set('pan', e.target.value || null)}
            />
          </Field>
          <Field label="CIN">
            <input
              className="mono"
              value={value.cin ?? ''}
              disabled={!editable}
              onChange={(e) => set('cin', e.target.value || null)}
            />
          </Field>
          <Field label="City">
            <input
              value={value.city ?? ''}
              disabled={!editable}
              onChange={(e) => set('city', e.target.value || null)}
            />
          </Field>
          <Field label="State code" caption="two digits">
            <input
              className="mono"
              value={value.state_code ?? ''}
              disabled={!editable}
              onChange={(e) => set('state_code', e.target.value || null)}
            />
          </Field>
        </div>
      </Card>

      <Card title="What we owe" hint="each duty switched on puts its deadlines on the calendar">
        <div className="duty-grid">
          {DUTIES.map((duty) => {
            const on = Boolean(value[duty.key])
            const why = blocked(duty)
            return (
              <label
                key={duty.key}
                className={`duty${on ? ' on' : ''}${why ? ' blocked' : ''}${editable ? '' : ' locked'}`}
              >
                <span className="duty-head">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!editable}
                    onChange={(e) => set(duty.key, e.target.checked as never)}
                  />
                  <span className="duty-label">{duty.label}</span>
                  <span className={`pill ${why ? 'advisory' : on ? 'approved' : 'draft'}`}>
                    {why ? 'schedules nothing' : on ? 'on' : 'off'}
                  </span>
                </span>
                <span className="duty-note">
                  {duty.note}
                  {duty.key === 'gst_registered' && on && gstins.length > 0 && (
                    <> Currently {gstins.join(', ')}.</>
                  )}
                </span>
                {why && <span className="duty-why">{why}</span>}
              </label>
            )
          })}
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <Field label="Profession tax state" caption="two letters, blank if not registered">
            <input
              className="mono"
              value={value.pt_state ?? ''}
              disabled={!editable}
              onChange={(e) => set('pt_state', e.target.value.toUpperCase() || null)}
            />
          </Field>
          <Field label="PTRC periodicity" caption="follows last year's liability">
            <select
              value={value.ptrc_periodicity ?? ''}
              disabled={!editable}
              onChange={(e) => set('ptrc_periodicity', e.target.value || null)}
            >
              <option value="">not registered</option>
              <option value="monthly">monthly</option>
              <option value="annual">annual</option>
            </select>
          </Field>
        </div>

        <p className="faint" style={{ marginBottom: 0 }}>
          Every change here is audited. Switching a duty on does not invent it — the calendar still
          needs the facts underneath, a GSTIN to file under or a payroll to withhold from, before it
          schedules anything.
          {!editable && ' Only an Admin may change these.'}
        </p>
      </Card>

      {editable && (changed.length > 0 || save.isSuccess) && (
        <div className="save-bar">
          {changed.length > 0 ? (
            <>
              <span>
                <strong>{changed.length}</strong> unsaved change{changed.length === 1 ? '' : 's'}
              </span>
              <span className="spacer" />
              <button className="quiet" disabled={save.isPending} onClick={() => setDraft(saved)}>
                Discard
              </button>
              <button
                className="primary"
                disabled={!value.name || !value.legal_name || save.isPending}
                onClick={() =>
                  save.mutate({
                    name: value.name,
                    legal_name: value.legal_name,
                    pan: value.pan,
                    cin: value.cin,
                    city: value.city,
                    state_code: value.state_code,
                    constitution: value.constitution,
                    pf_applicable: value.pf_applicable,
                    esi_applicable: value.esi_applicable,
                    pt_state: value.pt_state,
                    ptrc_periodicity: value.ptrc_periodicity,
                    ptec_applicable: value.ptec_applicable,
                    tds_deductor: value.tds_deductor,
                    gst_registered: value.gst_registered,
                    advance_tax_applicable: value.advance_tax_applicable,
                    msme_form1_applicable: value.msme_form1_applicable,
                    has_payroll: value.has_payroll,
                  })
                }
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <span className="muted">Saved. The compliance calendar follows from here.</span>
          )}
        </div>
      )}
    </>
  )
}
